import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Candidate,
  CandidateWithDetails,
  CreateCandidateDTO,
  UpdateCandidateDTO,
  FunnelStatus,
  LossReason,
} from '@/types';

interface CandidateFilters {
  status?: FunnelStatus;
  profile?: string;
  origin?: string;
  search?: string;
}

function buildQuery(filters?: CandidateFilters): string {
  if (!filters) return '';
  const parts: string[] = [];
  if (filters.status) parts.push(`status=${encodeURIComponent(filters.status)}`);
  if (filters.profile) parts.push(`profile=${encodeURIComponent(filters.profile)}`);
  if (filters.origin) parts.push(`origin=${encodeURIComponent(filters.origin)}`);
  if (filters.search) parts.push(`search=${encodeURIComponent(filters.search)}`);
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export function useCandidates(filters?: CandidateFilters) {
  return useQuery({
    queryKey: ['candidates', filters],
    queryFn: () => api.get<CandidateWithDetails[]>(`/api/candidates${buildQuery(filters)}`),
  });
}

export function useCandidate(id: string | null) {
  return useQuery({
    queryKey: ['candidates', id],
    queryFn: () => api.get<CandidateWithDetails>(`/api/candidates/${id}`),
    enabled: !!id,
  });
}

export function useCreateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateCandidateDTO) => {
      return api.post<Candidate>('/api/candidates', dto);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
}

export function useUpdateCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateCandidateDTO }) => {
      return api.put<Candidate>(`/api/candidates/${id}`, dto);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      qc.invalidateQueries({ queryKey: ['candidates', variables.id] });
    },
  });
}

export function usePatchCandidateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      loss_reason,
    }: {
      id: string;
      status: FunnelStatus;
      loss_reason?: LossReason | null;
    }) =>
      api.patch<Candidate>(`/api/candidates/${id}/status`, {
        funnel_status: status,
        loss_reason: loss_reason ?? null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
}

export function useDeleteCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/candidates/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
}
